import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { appelerApi, telechargerRessourceApi } from "../api/client";
import { utiliserSession } from "../auth/SessionContext";
import { OrigineBadge } from "../components/OrigineBadge";
import { formatDate } from "../utils/format";

interface Resultats {
  production_id: number;
  recettes_incluses: boolean;
  representations: Array<{
    id: number;
    date_heure: string;
    ville: string;
    capacite: number | null;
    origine: string;
    statistiques: null | {
      billets_vendus: number;
      taux_remplissage: string | null;
      note_moyenne: string | null;
      nombre_avis: number;
      origine: string;
    };
    recette?: { montant_brut: string; devise: string; origine: string };
  }>;
}

export function ResultatsPage() {
  const { id } = useParams();
  const { session } = utiliserSession();
  const [resultats, definirResultats] = useState<Resultats | null>(null);
  const [erreur, definirErreur] = useState("");

  function exporter(chemin: string, nom: string) {
    definirErreur("");
    void telechargerRessourceApi(chemin, nom).catch((cause: unknown) =>
      definirErreur(cause instanceof Error ? cause.message : "Export impossible")
    );
  }

  useEffect(() => {
    appelerApi<Resultats>(`/productions/${id}/statistiques`, {}, Boolean(session))
      .then(definirResultats)
      .catch((cause: Error) => definirErreur(cause.message));
  }, [id, session]);

  return (
    <section className="page resultats-page">
      <header className="page__entete">
        <Link className="retour" to={`/productions/${id}`}>← Retour à la fiche</Link>
        <span className="sur-titre sur-titre--sombre">Historique vérifiable</span>
        <h1>Représentations et résultats</h1>
        <p>
          Chaque ligne conserve sa source. Les données financières ne sont affichées
          qu’aux organisations autorisées.
        </p>
      </header>
      {erreur && <p className="alerte alerte--erreur">{erreur}</p>}
      {resultats?.recettes_incluses && (
        <>
          <p className="alerte alerte--privee">Vue privée : les recettes sont visibles dans cette session.</p>
          <div className="actions-export">
            <button
              className="bouton bouton--secondaire"
              type="button"
              onClick={() =>
                exporter(
                  `/productions/${id}/exports/resultats.csv`,
                  `resultats-${id}.csv`
                )
              }
            >
              Exporter le tableur
            </button>
            <button
              className="bouton bouton--secondaire"
              type="button"
              onClick={() =>
                exporter(
                  `/productions/${id}/exports/bilan-tournee.pdf`,
                  `bilan-tournee-${id}.pdf`
                )
              }
            >
              Générer le bilan PDF
            </button>
          </div>
        </>
      )}
      <div className="tableau-responsive">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Ville</th>
              <th>Jauge</th>
              <th>Billets</th>
              <th>Remplissage</th>
              <th>Avis</th>
              {resultats?.recettes_incluses && <th>Recette</th>}
              <th>Origine</th>
            </tr>
          </thead>
          <tbody>
            {resultats?.representations.map((representation) => (
              <tr key={representation.id}>
                <td>{formatDate(representation.date_heure)}</td>
                <td>{representation.ville}</td>
                <td>{representation.capacite ?? "—"}</td>
                <td>{representation.statistiques?.billets_vendus ?? "—"}</td>
                <td>
                  {representation.statistiques?.taux_remplissage
                    ? `${Number(representation.statistiques.taux_remplissage).toFixed(1)} %`
                    : "—"}
                </td>
                <td>{representation.statistiques?.nombre_avis ?? "—"}</td>
                {resultats.recettes_incluses && (
                  <td>
                    {representation.recette
                      ? `${Number(representation.recette.montant_brut).toLocaleString("fr-FR")} ${representation.recette.devise}`
                      : "—"}
                  </td>
                )}
                <td><OrigineBadge origine={representation.origine} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {resultats && !resultats.representations.length && (
        <div className="etat-vide">
          <strong>Aucune représentation raccordée pour le moment.</strong>
          <p>Les données Tickboss et Live apparaîtront ici après validation.</p>
        </div>
      )}
    </section>
  );
}
